import { Controller } from "stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Vervolg 1735: wijzigingen vanaf de desktop direct zichtbaar op het
// trainingsscherm, zonder de pagina te herladen. Eerst alleen voor de Duo
// PT-tablet; sinds ronde 3 ook voor het gewone tabletscherm (qrshow) en de
// training in de sporter-app. Eén mechanisme voor alle drie.
//
// De server vervangt bij een wijziging een leeg signaalelement
// (TabletKaartSignaal): kaart_signaal_<id> naast de oefeningkaart, of
// schema_signaal_<id> voor het hele schema. Hier zien we dat element
// verschijnen en halen we alleen die kaart op (of bij een oefening erbij of
// eraf het hele scherm of paneel), met de toegangsgegevens van deze pagina.
//
// Is de gebruiker in die kaart bezig (een veld heeft focus, een venster staat
// open of hij typte net), dan wacht het verversen tot hij klaar is; zijn invoer
// wordt nooit overschreven. Wijzigingen die van deze pagina zelf komen, dragen
// ons eigen kenmerk (X-Tablet-Bron) en worden niet opnieuw opgehaald. De
// scrollpositie blijft staan.
//
// Waarden:
//   kaartUrl    met __KAART__ op de plek van het oefening-id
//   paneelUrl   het hele scherm (of in Duo PT het paneel van deze sporter)
//   kaartPrefix het begin van het id van een oefeningkaart (tablet:
//               contentflex_, sporter-app: oefening-)
// Een target "urls" binnen de controller (data-kaart-url, data-paneel-url) gaat
// voor; het gewone tabletscherm wisselt van sessie zonder herladen en zet daar
// de adressen van de sessie die nu open staat.
const BRON = Math.random().toString(36).slice(2, 12).padEnd(8, "0")
const WACHT_MS = 250
const OPNIEUW_MS = 1000
const RUST_NA_INVOER_MS = 1500

let bronGezet = false
function zetBronOpVerzoeken() {
  if (bronGezet) return
  bronGezet = true
  document.addEventListener("turbo:before-fetch-request", (event) => {
    const opties = event.detail && event.detail.fetchOptions
    if (opties) opties.headers = Object.assign({}, opties.headers, { "X-Tablet-Bron": BRON })
  })
  document.addEventListener("ajax:beforeSend", (event) => {
    const xhr = event.detail && event.detail[0]
    if (xhr && typeof xhr.setRequestHeader === "function") xhr.setRequestHeader("X-Tablet-Bron", BRON)
  })
  // De streams die wij ophalen, verspringen het scherm niet: de scrollpositie
  // van het venster en van elk scrollend blok eromheen blijft gelijk.
  document.addEventListener("turbo:before-stream-render", (event) => {
    const stream = event.target
    if (!stream || !stream.hasAttribute || !stream.hasAttribute("data-tablet-live")) return
    const origineel = event.detail.render
    event.detail.render = async (element) => {
      const doel = document.getElementById(stream.getAttribute("target"))
      const posities = scrollPosities(doel)
      await origineel(element)
      herstelScroll(posities)
    }
  })
}

function scrollPosities(doel) {
  const posities = [{ venster: true, x: window.scrollX, y: window.scrollY }]
  let blok = doel && doel.parentElement
  while (blok) {
    if (blok.scrollTop > 0 || blok.scrollLeft > 0) posities.push({ blok, x: blok.scrollLeft, y: blok.scrollTop })
    blok = blok.parentElement
  }
  return posities
}

function herstelScroll(posities) {
  posities.forEach((p) => {
    if (p.venster) {
      if (window.scrollX !== p.x || window.scrollY !== p.y) window.scrollTo(p.x, p.y)
    } else if (p.blok.isConnected) {
      p.blok.scrollTop = p.y
      p.blok.scrollLeft = p.x
    }
  })
}

export default class extends Controller {
  static targets = ["kaartSignaal", "schemaSignaal", "urls", "naar"]
  static values = { kaartUrl: String, paneelUrl: String, kaartPrefix: { type: String, default: "contentflex_" } }

  connect() {
    zetBronOpVerzoeken()
    this.element.dataset.tabletLiveBron = BRON
    this.kaarten = new Set()
    this.paneel = false
    this.laatsteInvoer = new Map()
    this.onthoudInvoer = this.onthoudInvoer.bind(this)
    this.plan = this.plan.bind(this)
    ;["input", "change", "keydown", "submit"].forEach((soort) => this.element.addEventListener(soort, this.onthoudInvoer, true))
    this.element.addEventListener("focusout", this.plan)
    this.element.addEventListener("toggle", this.plan, true)
    // Klaar met oefeningen laadt een nieuwe pagina; het signaal van die
    // afronding hoeft deze pagina dan niet meer op te halen.
    this.weg = () => { this.verlaten = true }
    window.addEventListener("beforeunload", this.weg)
  }

  disconnect() {
    clearTimeout(this.timer)
    window.removeEventListener("beforeunload", this.weg)
    ;["input", "change", "keydown", "submit"].forEach((soort) => this.element.removeEventListener(soort, this.onthoudInvoer, true))
    this.element.removeEventListener("focusout", this.plan)
    this.element.removeEventListener("toggle", this.plan, true)
  }

  kaartSignaalTargetConnected(element) {
    if (!this.nieuwSignaal(element)) return
    this.kaarten.add(element.dataset.id)
    this.plan()
  }

  schemaSignaalTargetConnected(element) {
    if (!this.nieuwSignaal(element)) return
    this.paneel = true
    this.plan()
  }

  // Ronde 5 (1735): de training is elders afgerond. De server stuurt bij het
  // ophalen van het scherm het adres mee waar dit scherm na afronden op de
  // tablet of in de app zelf ook heen gaat (rapport of afgeronde training).
  naarTargetConnected(element) {
    const url = element.dataset.url || ""
    if (this.verlaten || !url.startsWith("/") || url.startsWith("//")) return
    this.verlaten = true
    clearTimeout(this.timer)
    // Een volledige pagina, net als Klaar met oefeningen (data-turbo=false);
    // vervangen, zodat terug niet bij de open training uitkomt.
    window.location.replace(url)
  }

  nieuwSignaal(element) {
    return Boolean(element.dataset.nieuw) && element.dataset.bron !== BRON
  }

  get kaartSelector() {
    return `[id^='${this.kaartPrefixValue}']`
  }

  kaartElement(id) {
    return document.getElementById(`${this.kaartPrefixValue}${id}`)
  }

  url(soort) {
    const urls = this.urlsTargets[this.urlsTargets.length - 1]
    const vanTarget = urls && urls.dataset[soort === "kaart" ? "kaartUrl" : "paneelUrl"]
    return vanTarget || (soort === "kaart" ? this.kaartUrlValue : this.paneelUrlValue)
  }

  onthoudInvoer(event) {
    const kaart = event.target.closest && event.target.closest(this.kaartSelector)
    if (kaart) this.laatsteInvoer.set(kaart.id, Date.now())
  }

  plan() {
    if (!this.paneel && this.kaarten.size === 0) return
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.verwerk(), WACHT_MS)
  }

  verwerk() {
    if (this.paneel) {
      if (this.bezig(this.element)) return this.later()
      this.paneel = false
      this.kaarten.clear()
      return this.haal(this.url("paneel"))
    }

    let wachten = false
    Array.from(this.kaarten).forEach((id) => {
      const kaart = this.kaartElement(id)
      if (!kaart || !this.element.contains(kaart)) { this.kaarten.delete(id); return }
      if (this.bezig(kaart)) { wachten = true; return }
      this.kaarten.delete(id)
      this.haal(this.url("kaart").replace("__KAART__", encodeURIComponent(id)), id)
    })
    if (wachten) this.later()
  }

  later() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.verwerk(), OPNIEUW_MS)
  }

  // Is de gebruiker hier aan het werk? Dan niets vervangen.
  bezig(gebied) {
    const actief = document.activeElement
    if (actief && actief !== document.body && gebied.contains(actief) && actief.matches("input, textarea, select, [contenteditable]")) {
      return true
    }
    if (gebied.querySelector("details[open]")) return true
    const kaarten = gebied.matches(this.kaartSelector) ? [gebied] : gebied.querySelectorAll(this.kaartSelector)
    return Array.from(kaarten).some((kaart) => Date.now() - (this.laatsteInvoer.get(kaart.id) || 0) < RUST_NA_INVOER_MS)
  }

  async haal(url, kaartId = null) {
    if (!url || this.verlaten) return
    try {
      const antwoord = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html", "X-Tablet-Bron": BRON },
        credentials: "same-origin"
      })
      // Alleen een Turbo Stream; een omleiding naar een gewone pagina (sessie
      // verlopen, training elders afgerond) laten we liggen.
      if (!antwoord.ok || !(antwoord.headers.get("Content-Type") || "").includes("turbo-stream")) return
      const html = await antwoord.text()
      // Begon de gebruiker intussen te typen? Dan straks opnieuw.
      const gebied = kaartId ? this.kaartElement(kaartId) : this.element
      if (gebied && this.bezig(gebied)) {
        if (kaartId) this.kaarten.add(kaartId)
        else this.paneel = true
        return this.later()
      }
      Turbo.renderStreamMessage(this.markeer(html))
    } catch (e) {
      // Geen verbinding: het volgende signaal of een herlaadbeurt haalt het in.
    }
  }

  // Markeer onze streams, zodat de scrollpositie bewaard blijft (zie boven).
  markeer(html) {
    const sjabloon = document.createElement("template")
    sjabloon.innerHTML = html
    sjabloon.content.querySelectorAll("turbo-stream").forEach((stream) => stream.setAttribute("data-tablet-live", ""))
    return sjabloon.innerHTML
  }
}
